/**
 * Générateur — Attendu 948 — Calculer le volume du cube, du pavé droit, du
 * prisme droit.
 * Niveau : 5ème | Domaine : Espace et géométrie / Grandeurs : volumes et unités
 *
 * Plan de cartes (version alignée sur la spécification 948 du 2026-09-21) :
 *
 *   Carte « Volume d'un cube » — saisie AVEC UNITÉ et QCM à quatre propositions
 *     typées (aire totale, somme des arêtes, bon nombre avec une unité d'aire).
 *   Carte « Volume d'un pavé droit » — idem, trois dimensions deux à deux
 *     distinctes, nommées longueur, largeur, hauteur dans cet ordre.
 *   Carte « Cubes empilés dans un pavé » — la décomposition V = aire de base ×
 *     hauteur, comptée en cubes unité.
 *   Carte « Volume d'un prisme droit » — base triangulaire (deux descriptions)
 *     ou trapézoïdale ; QCM dont les distracteurs sont l'oubli de la division
 *     par deux et la confusion des deux hauteurs.
 *   Carte « Pavé et prisme (vrai/faux) » — les formules et les unités.
 *   Carte « Volume : les trois solides » — niveau 5, mélange.
 *
 * ── Spécification 948 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   solides_concernes   liste fermée : cube, pavé droit, prisme droit
 *   arete_du_cube 2 .. 20 ; longueur_du_pave 4 .. 11 ; hauteur_du_pave 3 .. 11 ;
 *   profondeur_du_pave 2 .. 9 (les trois deux à deux distinctes)
 *   base_du_triangle_de_la_section_du_prisme 2 .. 11 ;
 *   hauteur_du_triangle_de_la_section_du_prisme 2 .. 6 ;
 *   hauteur_du_prisme 2 .. 10 ; nombre_de_cotes_de_la_base_du_prisme 3 .. 4
 *   nombre_de_decimales_des_dimensions 0 .. 1
 *   unite_de_longueur_des_donnees : m, dm, cm, mm (la MÊME dans tout l'énoncé)
 *   exposant_de_l_unite_de_la_reponse 3 (l'unité fait partie de la réponse)
 *
 * Exclusions appliquées : aucun cylindre, cône, pyramide ni boule (aucun
 * réglage n'y donne accès) ; deux dimensions d'un pavé jamais égales ; une
 * seule unité par énoncé, aucune conversion ; aucun distracteur égal à la bonne
 * réponse ni à un autre (retirage complet, jamais de bouche-trou) ; la réponse
 * n'est jamais un nombre nu ; aucun arrondi demandé (tous les volumes sont
 * exacts, calculés en arithmétique ENTIÈRE sur 1/2000 d'unité).
 *
 * Erreurs visées → mots-clés :
 *   Calculer l'aire totale à la place du volume        → aire_pour_volume
 *   Calculer la somme des arêtes ou un périmètre       → somme_des_aretes
 *   Oublier la division par deux de l'aire du triangle → oubli_division_par_deux
 *   Additionner les dimensions au lieu de les multiplier → addition_des_dimensions
 *   Confondre hauteur du prisme et hauteur du triangle → hauteurs_confondues
 *   Répondre sans unité, ou avec une unité de longueur ou d'aire → unite_manquante
 *   Appliquer aux décimaux une règle valable sur les entiers → regle_entiers_sur_decimaux
 *
 * Graduation → cartes :
 *   niveau 1 (cube, entiers, verbal)        cube_saisie, cube_qcm_unique
 *   niveau 2 (pavé, entiers, verbal)        pave_saisie, pave_qcm_unique, cubes_*
 *   niveau 3 (prisme à base triangulaire)   prisme_saisie, prisme_qcm_unique
 *   niveau 4 (prisme à base trapézoïdale)   prisme_saisie, prisme_qcm_unique
 *   niveau 5 (mélange)                      melange_qcm_unique
 *   réglage : dimensions entières ou à une décimale
 *   réglage : énoncé verbal ou solide coté en perspective (cube et pavé)
 *   réglage : saisie avec unité, ou quatre propositions
 *
 * ARITHMÉTIQUE EXACTE (vigilance 1) : toutes les dimensions vivent en DIXIÈMES
 * entiers et tout volume est un entier de 1/2000 d'unité cube — jamais un
 * flottant. Le dénominateur 2000 couvre la division par deux de l'aire du
 * triangle et les trois décimales d'un produit de trois dixièmes.
 */

import {
    randInt, randomChoice, shuffle, makeSaisie, makeTexteTrou, makeQcm, makeVraiFaux,
} from '../../../utils';
import { paveNomme } from './svgSolides';

const ATTENDU_ID = 948;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CUBE = 'Volume d’un cube';
const L_PAVE = 'Volume d’un pavé droit';
const L_CUBES = 'Cubes empilés dans un pavé';
const L_PRISME = 'Volume d’un prisme droit';
const L_PRISMEVF = 'Pavé et prisme (vrai/faux)';
const L_MELANGE = 'Volume : les trois solides';

// ─── Écriture exacte des nombres ─────────────────────────────────────────────

const DEN = 2000;                       // tout volume est un entier de 1/2000 d'unité cube

/**
 * Décimal EXACT d'un rationnel num/den dont le dénominateur ne contient que des
 * 2 et des 5 : on complète den en puissance de 10, jamais de virgule flottante
 * (vigilance 1). Écriture française, sans zéro final.
 */
const exact = (num, den) => {
    const signe = num < 0 ? '-' : '';
    let d = den, a = 0, b = 0;
    while (d % 2 === 0) { d /= 2; a++; }
    while (d % 5 === 0) { d /= 5; b++; }
    if (d !== 1) throw new Error(`gen_948 : denominateur non decimal (${den})`);
    const p = Math.max(a, b);
    const n = Math.abs(num) * (2 ** (p - a)) * (5 ** (p - b));
    const t = String(n).padStart(p + 1, '0');
    const ent = t.slice(0, t.length - p);
    const frac = p ? t.slice(t.length - p).replace(/0+$/, '') : '';
    return signe + (frac ? `${ent},${frac}` : ent);
};
/** Un nombre de DIXIÈMES écrit en toutes lettres françaises (« 4,5 », « 7 »). */
const frD = (d10) => exact(d10, 10);

// ─── Unités ──────────────────────────────────────────────────────────────────

// exclusion 3 : la MÊME unité dans tout l'énoncé, aucune conversion demandée.
const UNITES = ['mm', 'cm', 'dm', 'm'];

/**
 * Écritures admises d'un volume : la valeur dans l'unité de l'énoncé et dans
 * les deux unités voisines (vigilance 3 : « 0,125 dm³ vaut 125 cm³ »), chacune
 * avec les trois façons d'écrire l'exposant (³, 3, ^3). Liste FINIE et auditable.
 */
const ecrituresVolume = (num, i) => {
    const out = [];
    [i - 1, i, i + 1].forEach((j) => {
        if (j < 0 || j >= UNITES.length) return;
        // passer à l'unité voisine multiplie ou divise le volume par 1000
        const n = j < i ? num * 1000 : num;
        const d = j > i ? DEN * 1000 : DEN;
        const v = exact(n, d);
        ['³', '3', '^3'].forEach((e) => out.push(`${v} ${UNITES[j]}${e}`));
    });
    return out;
};
const volumeCanonique = (num, i) => `${exact(num, DEN)} ${UNITES[i]}³`;

const MENTION_UNITE = '<p><i>Une unité est attendue dans la réponse.</i></p>';

// ─── Tirages (bornes de la spec) ─────────────────────────────────────────────

/** Une dimension entière ou à UNE décimale, en dixièmes. */
const dim10 = (min, max, decimal) => {
    if (!decimal) return randInt(min, max) * 10;
    for (let essai = 0; essai < 40; essai++) {
        const v = randInt(min * 10 + 1, max * 10 - 1);
        if (v % 10 !== 0) return v;                       // une vraie décimale
    }
    return min * 10 + 5;
};

/** Cube : arête 2 à 20. Volume = c³ (en 1/2000 d'unité cube). */
const tirerCube = (decimal) => {
    const c = dim10(2, decimal ? 9 : 20, decimal);
    return { solide: 'cube', c, dims: [c, c, c] };
};

/** Pavé : longueur 4 à 11, largeur 2 à 9, hauteur 3 à 11, deux à deux distinctes. */
const tirerPave = (decimal) => {
    for (let essai = 0; essai < 120; essai++) {
        const L = dim10(4, 11, decimal && randInt(0, 2) === 0);
        const la = dim10(2, 9, decimal && randInt(0, 2) === 0);
        const h = dim10(3, 11, decimal && randInt(0, 2) === 0);
        if (new Set([L, la, h]).size === 3) return { solide: 'pave', L, la, h, dims: [L, la, h] };
    }
    return { solide: 'pave', L: 110, la: 20, h: 70, dims: [110, 20, 70] };
};

/**
 * Prisme droit. Trois descriptions de base tirées au sort :
 *   - 'rectangle' : triangle rectangle donné par les deux côtés de l'angle droit
 *   - 'quelconque' : triangle donné par une base et la hauteur relative
 *   - 'trapeze'   : trapèze donné par ses deux bases parallèles et sa hauteur
 * `bTri` est la base du triangle, `hTri` la hauteur DU TRIANGLE, `hPrisme` la
 * hauteur DU PRISME (vigilance 5 : chaque variable porte ce qu'elle désigne).
 */
const tirerPrisme = (decimal, forme) => {
    for (let essai = 0; essai < 120; essai++) {
        const bTri = dim10(2, 11, decimal && randInt(0, 2) === 0);
        const hTri = dim10(2, 6, decimal && randInt(0, 2) === 0);
        const bTri2 = dim10(2, 11, false);
        const hPrisme = dim10(2, 10, decimal && randInt(0, 2) === 0);
        if (new Set([bTri, hTri, hPrisme]).size < 3) continue;
        if (forme === 'trapeze' && (bTri2 === bTri || bTri2 === hTri || bTri2 === hPrisme)) continue;
        return { solide: 'prisme', forme, bTri, hTri, bTri2, hPrisme };
    }
    return { solide: 'prisme', forme, bTri: 70, hTri: 30, bTri2: 50, hPrisme: 40 };
};

/**
 * Numérateur EXACT du volume, sur DEN = 2000, toutes dimensions en dixièmes :
 *   cube    V = (c/10)³              = 2c³ / 2000
 *   pavé    V = (L/10)(l/10)(h/10)   = 2·L·l·h / 2000
 *   prisme  V = (b·ht/2/100)·(H/10)  = b·ht·H / 2000
 *   trapèze V = ((b+b')·ht/2/100)·(H/10) = (b+b')·ht·H / 2000
 */
const volumeNum = (s) => {
    if (s.solide === 'cube') return 2 * s.c * s.c * s.c;
    if (s.solide === 'pave') return 2 * s.L * s.la * s.h;
    if (s.forme === 'trapeze') return (s.bTri + s.bTri2) * s.hTri * s.hPrisme;
    return s.bTri * s.hTri * s.hPrisme;
};
/** Numérateur de l'AIRE DE BASE du prisme, sur 200 (en unité carrée). */
const aireBaseNum = (s) => (s.forme === 'trapeze'
    ? (s.bTri + s.bTri2) * s.hTri
    : s.bTri * s.hTri);

// ─── Figures : le solide coté en perspective (réglage transverse) ────────────

const figEnonce = (svg, alt) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'small', alt } }],
});

const figurePave = (s, u) => {
    const [L, la, h] = s.dims;
    const ech = 5 / Math.max(L, la, h);
    return figEnonce(paveNomme(
        { L: L * ech + 1.2, H: h * ech + 1.2, P: la * ech + 1 },
        {
            vb: { w: 230, h: 200 }, labels: false, salt: randInt(0, 999999),
            coter: { L: `${frD(L)} ${u}`, H: `${frD(h)} ${u}`, P: `${frD(la)} ${u}` },
        },
    ).svg, s.solide === 'cube' ? 'Cube coté en perspective cavalière' : 'Pavé droit coté en perspective cavalière');
};

// ─── Énoncés ─────────────────────────────────────────────────────────────────

const enonceCube = (s, u, figure) => (figure
    ? '<p>Voici un cube, dessiné en perspective cavalière avec son arête cotée.</p>'
    : `<p>Un cube a pour arête <b>${frD(s.c)} ${u}</b>.</p>`);

const enoncePave = (s, u, figure) => (figure
    ? '<p>Voici un pavé droit, dessiné en perspective cavalière avec ses dimensions cotées.</p>'
    : `<p>Un pavé droit a pour longueur <b>${frD(s.L)} ${u}</b>, pour largeur <b>${frD(s.la)} ${u}</b> `
      + `et pour hauteur <b>${frD(s.h)} ${u}</b>.</p>`);

const enoncePrisme = (s, u) => {
    if (s.forme === 'rectangle') {
        return '<p>Un prisme droit a pour base un <b>triangle rectangle</b> dont les côtés de l\'angle droit '
            + `mesurent <b>${frD(s.bTri)} ${u}</b> et <b>${frD(s.hTri)} ${u}</b>. `
            + `La hauteur du prisme est de <b>${frD(s.hPrisme)} ${u}</b>.</p>`;
    }
    if (s.forme === 'trapeze') {
        return '<p>Un prisme droit a pour base un <b>trapèze</b> dont les deux côtés parallèles mesurent '
            + `<b>${frD(s.bTri)} ${u}</b> et <b>${frD(s.bTri2)} ${u}</b>, et dont la hauteur mesure `
            + `<b>${frD(s.hTri)} ${u}</b>. La hauteur du prisme est de <b>${frD(s.hPrisme)} ${u}</b>.</p>`;
    }
    return '<p>Un prisme droit a pour base un <b>triangle</b> dont un côté mesure '
        + `<b>${frD(s.bTri)} ${u}</b> et dont la hauteur relative à ce côté mesure <b>${frD(s.hTri)} ${u}</b>. `
        + `La hauteur du prisme est de <b>${frD(s.hPrisme)} ${u}</b>.</p>`;
};

const enonce = (s, u, figure) => (s.solide === 'cube' ? enonceCube(s, u, figure)
    : (s.solide === 'pave' ? enoncePave(s, u, figure) : enoncePrisme(s, u)));

const figureDe = (s, u, figure) => (figure && s.solide !== 'prisme' ? figurePave(s, u) : null);

// ─── Distracteurs typés (exclusion 4, vigilance 4) ───────────────────────────

/**
 * Pool de distracteurs CODÉS pour un solide donné. Chaque valeur porte son
 * unité : une réponse « 125 cm² » n'est pas à moitié juste, c'est l'erreur
 * visée (vigilance 8).
 */
const poolDistracteurs = (s, i) => {
    const u = UNITES[i];
    const out = [];
    if (s.solide === 'cube') {
        const c = s.c;
        out.push({ code: 'aire_pour_volume', valeur: `${exact(6 * c * c, 100)} ${u}³` });
        out.push({ code: 'somme_des_aretes', valeur: `${exact(12 * c, 10)} ${u}³` });
        out.push({ code: 'addition_des_dimensions', valeur: `${exact(3 * c, 10)} ${u}³` });
        out.push({ code: 'unite_manquante', valeur: `${exact(volumeNum(s), DEN)} ${u}²` });
    } else if (s.solide === 'pave') {
        const { L, la, h } = s;
        out.push({ code: 'aire_pour_volume', valeur: `${exact(2 * (L * la + L * h + la * h), 100)} ${u}³` });
        out.push({ code: 'somme_des_aretes', valeur: `${exact(4 * (L + la + h), 10)} ${u}³` });
        out.push({ code: 'addition_des_dimensions', valeur: `${exact(L + la + h, 10)} ${u}³` });
        out.push({ code: 'unite_manquante', valeur: `${exact(volumeNum(s), DEN)} ${u}²` });
    } else {
        const aire = aireBaseNum(s);
        out.push({ code: 'oubli_division_par_deux', valeur: `${exact(2 * volumeNum(s), DEN)} ${u}³` });
        out.push({ code: 'aire_pour_volume', valeur: `${exact(aire, 200)} ${u}³` });
        out.push({ code: 'hauteurs_confondues', valeur: `${exact(aire * s.hTri, 2000)} ${u}³` });
        out.push({ code: 'unite_manquante', valeur: `${exact(volumeNum(s), DEN)} ${u}²` });
    }
    return out;
};

/**
 * Trois distracteurs distincts, jamais égaux à la bonne réponse (retirage
 * complet, jamais de bouche-trou). L'aire à la place du volume est TOUJOURS
 * proposée — « la confusion entre aire et volume doit être proposée à chaque
 * question, pour chaque solide » (intention_pedagogique) — et, pour le prisme,
 * l'oubli de la division par deux l'est aussi.
 */
const PRIORITAIRES = { cube: ['aire_pour_volume'], pave: ['aire_pour_volume'], prisme: ['aire_pour_volume', 'oubli_division_par_deux'] };

const troisDistracteurs = (s, i, correct) => {
    const pool = poolDistracteurs(s, i);
    const prioritaires = PRIORITAIRES[s.solide];
    const ordonne = [
        ...prioritaires.map((c) => pool.find((d) => d.code === c)).filter(Boolean),
        ...shuffle(pool.filter((d) => !prioritaires.includes(d.code))),
    ];
    const vus = new Set([correct]);
    const gardes = [];
    ordonne.forEach((d) => {
        if (gardes.length < 3 && !vus.has(d.valeur)) { vus.add(d.valeur); gardes.push(d); }
    });
    return gardes.length === 3 ? gardes : null;
};

// ═══════════════════════════════════════════════════════════
// Cartes de calcul : saisie avec unité, et QCM à quatre propositions
// ═══════════════════════════════════════════════════════════

const NIVEAU = { cube: 'n1', pave: 'n2' };
const niveauDe = (s) => (s.solide === 'prisme' ? (s.forme === 'trapeze' ? 'n4' : 'n3') : NIVEAU[s.solide]);

const tirerSolide = (quoi, decimal) => {
    if (quoi === 'cube') return tirerCube(decimal);
    if (quoi === 'pave') return tirerPave(decimal);
    return tirerPrisme(decimal, quoi === 'trapeze' ? 'trapeze' : randomChoice(['rectangle', 'quelconque']));
};

const genVolumeSaisie = (label, tireur, niveauForce) => () => {
    const decimal = randomChoice([false, false, true]);
    const s = tireur(decimal);
    const i = randInt(0, UNITES.length - 1);
    const u = UNITES[i];
    const figure = s.solide !== 'prisme' && randomChoice([false, true]);
    const num = volumeNum(s);
    const bonne = volumeCanonique(num, i);
    const texte = `${enonce(s, u, figure)}<p><b>Quel est son volume ?</b></p>${MENTION_UNITE}`;
    const payload = makeSaisie(bonne, texte, {
        ...opts(label), suffixe: '',
        comparaison: 'expression', reponses_acceptees: ecrituresVolume(num, i),
        elements_additionnels: figureDe(s, u, figure),
    });
    payload._meta.params = {
        niveau: niveauForce || niveauDe(s), solide: s.solide, forme: s.forme || null,
        unite: u, decimal, figure: !!figure, volumeNum: num,
        dimensions: s.dims || [s.bTri, s.hTri, s.hPrisme],
        erreursVisees: ['unite_manquante', ...(decimal ? ['regle_entiers_sur_decimaux'] : [])],
    };
    return payload;
};

const genVolumeQcm = (label, tireur, niveauForce) => () => {
    for (let essai = 0; essai < 60; essai++) {
        const decimal = randomChoice([false, false, true]);
        const s = tireur(decimal);
        const i = randInt(0, UNITES.length - 1);
        const u = UNITES[i];
        const figure = s.solide !== 'prisme' && randomChoice([false, true]);
        const num = volumeNum(s);
        const bonne = volumeCanonique(num, i);
        const distracteurs = troisDistracteurs(s, i, bonne);
        if (!distracteurs) continue;                    // jamais de bouche-trou : on retire tout
        // Les valeurs particulières où l'aire totale égale le volume (arête 6
        // pour un cube) font disparaître le distracteur prioritaire : on rejoue
        // le tirage plutôt que de servir une question sans la confusion
        // aire/volume, que l'intention veut à CHAQUE question (exclusion 4).
        const codes = distracteurs.map((d) => d.code);
        if (!PRIORITAIRES[s.solide].every((c) => codes.includes(c))) continue;
        const texte = `${enonce(s, u, figure)}<p><b>Quel est son volume ?</b></p>`;
        const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne, texte, {
            ...opts(label), elements_additionnels: figureDe(s, u, figure),
        });
        payload._meta.params = {
            niveau: niveauForce || niveauDe(s), solide: s.solide, forme: s.forme || null,
            unite: u, decimal, figure: !!figure, volumeNum: num,
            dimensions: s.dims || [s.bTri, s.hTri, s.hPrisme],
            distracteurs,
            erreursVisees: decimal ? ['regle_entiers_sur_decimaux'] : [],
        };
        return payload;
    }
    // repli déterministe : un cube d'arête 5, aucun distracteur ne peut coïncider
    const s = { solide: 'cube', c: 50, dims: [50, 50, 50] };
    const num = volumeNum(s);
    const bonne = volumeCanonique(num, 1);
    const distracteurs = poolDistracteurs(s, 1).slice(0, 3);
    const payload = makeQcm([bonne, ...distracteurs.map((d) => d.valeur)], bonne,
        `${enonce(s, 'cm', false)}<p><b>Quel est son volume ?</b></p>`, opts(label));
    payload._meta.params = {
        niveau: niveauForce || 'n1', solide: 'cube', forme: null, unite: 'cm',
        decimal: false, figure: false, volumeNum: num, dimensions: s.dims,
        distracteurs, erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Cubes empilés : V = aire de base × hauteur, comptée
// ═══════════════════════════════════════════════════════════

const genCubes = () => {
    const s = tirerPave(false);
    const u = UNITES[randInt(0, UNITES.length - 1)];
    const L = s.L / 10, la = s.la / 10, h = s.h / 10;
    const parCouche = L * la;
    const phrase = `Nombre de cubes par couche : {{${parCouche}}}\nNombre de couches : {{${h}}}\n`
        + `Nombre de cubes en tout : {{${parCouche * h}}}`;
    const texte = `<p>Un pavé droit a pour longueur <b>${L} ${u}</b>, pour largeur <b>${la} ${u}</b> `
        + `et pour hauteur <b>${h} ${u}</b>. On le remplit exactement de cubes de <b>1 ${u}</b> d'arête.</p>`
        + '<p><b>Complète.</b></p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CUBES), mode: 'saisie', strict: false });
    payload._meta.params = {
        niveau: 'n2', solide: 'pave', forme: null, unite: u, decimal: false, figure: false,
        dimensions: s.dims, volumeNum: volumeNum(s),
        erreursVisees: ['aire_pour_volume'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Pavé et prisme (vrai/faux) : formules et unités
// ═══════════════════════════════════════════════════════════

const VF_VRAIES = [
    'Un pavé droit est un prisme droit particulier.',
    'Un cube est un pavé droit particulier.',
    'Le volume d’un prisme droit est égal à l’aire de sa base multipliée par sa hauteur.',
    'Le volume d’un cube d’arête 5 cm est 125 cm³.',
    'Un volume s’exprime avec une unité dont l’exposant est 3.',
];
const VF_FAUSSES = [
    'Toutes les faces d’un prisme droit sont des rectangles.',
    'Le volume d’un prisme droit est égal au périmètre de sa base multiplié par sa hauteur.',
    'Le volume d’un cube d’arête 5 cm est 25 cm³.',
    'Un volume peut s’exprimer en cm².',
    'Pour un prisme à base triangulaire, le volume est la base du triangle multipliée par sa hauteur puis par la hauteur du prisme.',
];

const genPrismeVF = () => {
    const vraies = shuffle(VF_VRAIES).slice(0, 2);
    const fausses = shuffle(VF_FAUSSES).slice(0, 2);
    const lignes = shuffle([...vraies.map((t) => [t, 0]), ...fausses.map((t) => [t, 1])]);
    const payload = makeVraiFaux(lignes.map((x) => x[0]), lignes.map((x) => x[1]),
        '<p>Pour chaque affirmation, indique si elle est <b>vraie</b> ou <b>fausse</b>.</p>',
        opts(L_PRISMEVF));
    payload._meta.params = {
        niveau: 'n5', solide: 'prisme', forme: null, unite: 'cm', decimal: false, figure: false,
        erreursVisees: ['aire_pour_volume', 'somme_des_aretes', 'unite_manquante', 'oubli_division_par_deux'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const tirerMelange = (decimal) => tirerSolide(randomChoice(['cube', 'pave', 'prisme', 'trapeze']), decimal);

const GENERATORS = {
    cube_saisie: genVolumeSaisie(L_CUBE, (d) => tirerCube(d)),
    cube_qcm_unique: genVolumeQcm(L_CUBE, (d) => tirerCube(d)),
    pave_saisie: genVolumeSaisie(L_PAVE, (d) => tirerPave(d)),
    pave_qcm_unique: genVolumeQcm(L_PAVE, (d) => tirerPave(d)),
    cubes_texte_trou_saisie: genCubes,
    prisme_saisie: genVolumeSaisie(L_PRISME, (d) => tirerSolide(randomChoice(['prisme', 'trapeze']), d)),
    prisme_qcm_unique: genVolumeQcm(L_PRISME, (d) => tirerSolide(randomChoice(['prisme', 'trapeze']), d)),
    prismeVF_qcm: genPrismeVF,
    melange_qcm_unique: genVolumeQcm(L_MELANGE, tirerMelange, 'n5'),
};

const FORMAT_LABELS = {
    saisie: 'Saisie', texte_trou_saisie: 'Saisie', qcm_unique: 'QCM', qcm: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'cube', label: L_CUBE, formatsList: buildFormats('cube') },
    { id: 'pave', label: L_PAVE, formatsList: buildFormats('pave') },
    { id: 'cubes', label: L_CUBES, formatsList: buildFormats('cubes') },
    { id: 'prisme', label: L_PRISME, formatsList: buildFormats('prisme') },
    { id: 'prismeVF', label: L_PRISMEVF, formatsList: buildFormats('prismeVF') },
    { id: 'melange', label: L_MELANGE, formatsList: buildFormats('melange') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { exact, volumeNum, aireBaseNum, ecrituresVolume, DEN, UNITES };
